import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import classes from "./SmallAlert.module.css";

const SmallAlert = (props) => {
  const {
    autoDismissOnInteraction,
    onDismiss,
    persist,
    dismissOnClickOutside,
    dismissOnFocusOutside,
  } = props;

  // Backwards-compatible behavior:
  // - persist=true disables all auto-dismiss
  // - autoDismissOnInteraction controls both focus and click when provided
  // - Otherwise: click outside dismisses by default (true), focus does not (false)
  const effectiveDismissOnClick = persist
    ? false
    : autoDismissOnInteraction !== undefined
    ? autoDismissOnInteraction
    : dismissOnClickOutside !== undefined
    ? dismissOnClickOutside
    : false;

  const effectiveDismissOnFocus = persist
    ? false
    : autoDismissOnInteraction !== undefined
    ? autoDismissOnInteraction
    : dismissOnFocusOutside !== undefined
    ? dismissOnFocusOutside
    : false;
  const alertRef = useRef(null);
  const [visible, setVisible] = useState(true);
  const [isHiding, setIsHiding] = useState(false);

  // If message changes, show the alert again
  useEffect(() => {
    setIsHiding(false);
    setVisible(true);
  }, [props.message]);

  const dismiss = useCallback(() => {
    if (isHiding || !visible) return;
    setIsHiding(true);
    const durationMs = 350; // match CSS
    setTimeout(() => {
      if (typeof onDismiss === "function") {
        onDismiss();
      }
      setVisible(false);
      setIsHiding(false);
    }, durationMs);
  }, [isHiding, onDismiss, visible]);

  // Auto-dismiss on global user interaction (focus and/or click) based on props
  useEffect(() => {
    if (!visible) return;
    if (!effectiveDismissOnClick && !effectiveDismissOnFocus) return;

    const handleGlobalInteraction = (e) => {
      if (alertRef.current && alertRef.current.contains(e.target)) return;
      dismiss();
    };

    if (effectiveDismissOnFocus) {
      document.addEventListener("focusin", handleGlobalInteraction, true);
    }
    if (effectiveDismissOnClick) {
      document.addEventListener("click", handleGlobalInteraction, true);
    }

    return () => {
      if (effectiveDismissOnFocus) {
        document.removeEventListener("focusin", handleGlobalInteraction, true);
      }
      if (effectiveDismissOnClick) {
        document.removeEventListener("click", handleGlobalInteraction, true);
      }
    };
  }, [effectiveDismissOnClick, effectiveDismissOnFocus, visible, dismiss]);

  if (!visible) return null;
  let alert = "";
  if (props.type === "error") {
    alert = classes.error;
  }
  if (props.type === "info") {
    alert = classes.info;
  }
  if (props.type === "warning") {
    alert = classes.warning;
  }
  if (props.type === "success") {
    alert = classes.success;
  }

  /*** We are going to use alerts as container for notifications(notices) which are links ***/
  let render = "";
  if (props.alert) {
    render = (
      <div
        ref={alertRef}
        className={`${classes.note} ${alert} ${
          isHiding ? classes.fadeOut : classes.fadeIn
        } ${props.dynamicSize ? classes.dynamicSize : ""}`}
        style={{ marginBottom: 5, paddingRight: 15, ...props.style }}
      >
        <div>
          <p>{props.message}</p>
        </div>
      </div>
    );
  } else {
    render = (
      <Link
        to={`/${props.link}`}
        className={`${classes.note} ${alert} ${
          isHiding ? classes.fadeOut : classes.fadeIn
        } ${props.dynamicSize ? classes.dynamicSize : ""}`}
        style={{ marginBottom: 5, paddingRight: 15, ...props.style }}
      >
        <div>
          <h4>{props.title}</h4>
          {props.date && <h6>{props.date}</h6>}
          <p>{props.message}</p>
        </div>
      </Link>
    );
  }
  return render;
};

export default SmallAlert;
